import React, { useState, useEffect, memo } from "react";
import styled from "styled-components";

interface CustomSwitchProps {
  label?: string;
  name?: string;
  disabled?: boolean;
  /** When provided, switch is controlled: display follows this value, no optimistic toggle (use when confirmation/API must succeed before UI updates). */
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  onApiUpdate?: (status: boolean) => Promise<void>;
  isRequired?: boolean;
}

const SwitchWrapper = styled.div<{ $checked: boolean; $disabled?: boolean }>`
  position: relative;
  width: 35px;
  height: 20px;
  background: ${({ $checked }) => ($checked ? "#000000" : "#ccc")};
  border-radius: 20px;
  transition: background 0.3s;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

const Slider = styled.div<{ $checked: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $checked }) => ($checked ? "18px" : "3px")};
  width: 14px;
  height: 14px;
  background: #fff;
  border-radius: 50%;
  transition: left 0.3s;
`;

const CustomSwitchComponent: React.FC<CustomSwitchProps> = ({
  label,
  disabled,
  checked: controlledChecked,
  defaultChecked = false,
  onChange,
  onApiUpdate,
  isRequired = false,
}) => {
  const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked);
  const isControlled = controlledChecked !== undefined;
  const displayValue = isControlled ? controlledChecked : uncontrolledChecked;

  useEffect(() => {
    if (!isControlled) {
      setUncontrolledChecked(defaultChecked);
    }
  }, [isControlled, defaultChecked]);

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;

    const newVal = !displayValue;

    if (isControlled) {
      // Controlled: only notify parent; UI stays as-is until parent updates `checked`
      onChange?.(newVal);
      return;
    }

    setUncontrolledChecked(newVal);
    try {
      if (onApiUpdate) {
        await onApiUpdate(newVal);
      }
    } catch (error) {
      console.error("API update failed:", error);
      setUncontrolledChecked(displayValue);
    }
    onChange?.(newVal);
  };

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontFamily: "Poppins",
      }}
    >
      <SwitchWrapper $checked={displayValue} $disabled={disabled} onClick={handleToggle}>
        <Slider $checked={displayValue} />
      </SwitchWrapper>
      {label && (
        <span
          style={{
            fontSize: "13px",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span> 
      )}
      {isRequired && (
        <span
          style={{
            color: "red",
          }}
        >
          *
        </span>
      )}
    </div>
  );
};

export const CustomSwitch = memo(CustomSwitchComponent);